/*
 * Vendored from React Bits (https://github.com/DavidHDev/react-bits), src/ts-default/Micro/LatticeLoader/LatticeLoader.tsx
 * Upstream commit e1bbb696fc53f7f91e694c529e4d68c899773b6e (2026-09-30). Licence: MIT + Commons Clause, see ../LICENSE.md.
 * Use only inside this product; do not redistribute these components as a kit or package.
 * Change: openspec/changes/add-subtle-portal-micro-interactions.
 * Local patches:
 *   1. TypeScript 4.9: explicit type predicate in resolvePattern (upstream relies on TS 5.5 inference).
 *   2. Performance: the stopwatch interval only runs when showTimer is true.
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './LatticeLoader.css';
const PATTERNS = {
    arrow: { 3: { cells: [1, 2, 3, 0, 1, 2, 1, 2, 3], loop: 7.2, scale: 1 } },
    dots: { 3: { cells: [0, 1, 2, 0, 1, 2, 0, 1, 2], loop: 3, scale: 2.4 } },
    ripple: { 3: { cells: [2, 1, 2, 1, 0, 1, 2, 1, 2], loop: 4.8, scale: 1.5 } },
    spiral: { 3: { cells: [0, 1, 2, 7, 8, 3, 6, 5, 4], loop: 9, scale: 1.2, lit: 0.35 } },
    orbit: {
        3: { cells: [0, 1, 2, 7, null, 3, 6, 5, 4], loop: 8, scale: 1.2 },
        4: { cells: [0, 1, 2, 3, 11, null, null, 4, 10, null, null, 5, 9, 8, 7, 6], loop: 6, scale: 1.2, lit: 0.45 }
    },
    snake: {
        3: { cells: [0, 1, 2, 5, 4, 3, 6, 7, 8], loop: 9, scale: 1, lit: 0.35 },
        4: { cells: [0, 1, 2, 3, 7, 6, 5, 4, 8, 9, 10, 11, 15, 14, 13, 12], loop: 16, scale: 1, lit: 0.25 }
    },
    sweep: { 4: { cells: [0, 1, 2, 3, 1, 2, 3, 4, 2, 3, 4, 5, 3, 4, 5, 6], loop: 5, scale: 1, lit: 0.45 } },
    spin: { 4: { cells: [0, 0, 1, 1, 0, 0, 1, 1, 3, 3, 2, 2, 3, 3, 2, 2], loop: 4, scale: 1.6, lit: 0.35 } },
    rain: { 4: { cells: [0, 2, 1, 3, 1, 3, 2, 4, 2, 4, 3, 5, 3, 5, 4, 6], loop: 4, scale: 1.2, lit: 0.35 } },
    pulse: { 4: { cells: [2, 1, 1, 2, 1, 0, 0, 1, 1, 0, 0, 1, 2, 1, 1, 2], loop: 2.4, scale: 2.5, lit: 0.45 } }
};
const DEFAULT_PATTERN = { 3: 'orbit', 4: 'sweep' };
const MARKS = {
    3: { done: [2, 3, 5, 7], error: [0, 2, 4, 6, 8] },
    4: { done: [7, 8, 10, 13], error: [0, 3, 5, 6, 9, 10, 12, 15] }
};
const resolvePattern = (pattern, grid) => {
    if (typeof pattern === 'string') {
        const named = PATTERNS[pattern];
        return (named && named[grid]) || PATTERNS[DEFAULT_PATTERN[grid]][grid];
    }
    const cells = Array.from({ length: grid * grid }, (_, i) => pattern.cells[i] ?? null);
    const max = Math.max(0, ...cells.filter((v) => v != null));
    return { cells, loop: pattern.loop ?? max + 4.2, scale: pattern.scale ?? 1, lit: pattern.lit ?? 0.62 };
};
const fmt = (ds) => ds < 600 ? `${(ds / 10).toFixed(1)}s` : `${Math.floor(ds / 600)}m ${((ds % 600) / 10).toFixed(1)}s`;
const spoken = (ds) => ds < 600
    ? `${(ds / 10).toFixed(1)} seconds`
    : `${Math.floor(ds / 600)} minutes ${((ds % 600) / 10).toFixed(1)} seconds`;
const LatticeLoader = ({ label = 'Thinking', doneLabel = 'Done in', errorLabel = 'Failed after', status = 'working', pattern = 'orbit', grid = 3, shape = 'round', color = 'currentColor', doneColor = '#22c55e', errorColor = '#ef4444', cellSize = 6, gap = 2, fontSize = 14, step = 90, idleOpacity = 0.15, glow = false, glowColor = '', showTimer = true, elapsed, className = '', style }) => {
    const n = grid === 4 ? 4 : 3;
    const pat = resolvePattern(pattern, n);
    const marks = MARKS[n];
    const d = step * pat.scale;
    const cycle = Math.round(pat.loop * d);
    const timerRef = useRef(null);
    const dsRef = useRef(0);
    const markRef = useRef('done');
    const mark = status === 'working' ? markRef.current : status;
    markRef.current = mark;
    const [announce, setAnnounce] = useState(`${label}, in progress`);
    const paint = (ds) => {
        dsRef.current = ds;
        if (timerRef.current)
            timerRef.current.textContent = fmt(ds);
    };
    useLayoutEffect(() => {
        if (elapsed != null) {
            paint(Math.round(elapsed * 10));
            return undefined;
        }
        if (status !== 'working' || !showTimer)
            return undefined;
        const startedAt = performance.now();
        paint(0);
        const id = setInterval(() => paint(Math.floor((performance.now() - startedAt) / 100)), 100);
        return () => clearInterval(id);
    }, [status, elapsed, showTimer]);
    useEffect(() => {
        if (status === 'working')
            setAnnounce(`${label}, in progress`);
        else
            setAnnounce(`${status === 'done' ? doneLabel : errorLabel}${showTimer ? ` ${spoken(dsRef.current)}` : ''}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);
    return (_jsxs("span", { role: "status", className: `lattice-loader${className ? ` ${className}` : ''}`, "data-status": status, "data-shape": shape, "data-glow": glow ? '' : undefined, style: {
            '--ll-n': n,
            '--ll-cell': `${cellSize}px`,
            '--ll-gap': `${gap}px`,
            '--ll-font': `${fontSize}px`,
            '--ll-color': color,
            '--ll-mark': status === 'error' ? errorColor : doneColor,
            '--ll-idle': idleOpacity,
            '--ll-glow': glowColor || color,
            '--ll-mark-glow': glowColor || (status === 'error' ? errorColor : doneColor),
            '--ll-cycle': `${cycle}ms`,
            ...style
        }, children: [_jsxs("span", { className: "lattice-loader__grid", "aria-hidden": "true", children: [_jsx("span", { className: "lattice-loader__layer lattice-loader__run", children: pat.cells.map((unit, i) => (_jsx("span", { className: "lattice-loader__cell", "data-hole": unit == null ? '' : undefined, "data-lit": pat.lit && pat.lit !== 0.62 ? Math.round(pat.lit * 100) : undefined, style: unit == null ? undefined : { animationDelay: `${Math.round(unit * d)}ms` } }, i))) }), _jsx("span", { className: "lattice-loader__layer lattice-loader__mark", children: pat.cells.map((_, i) => (_jsx("span", { className: "lattice-loader__cell", "data-on": marks[mark].includes(i) ? '' : undefined }, i))) })] }), _jsxs("span", { className: "lattice-loader__label", "aria-hidden": "true", children: [_jsx("span", { className: "lattice-loader__text", "data-active": status === 'working' ? '' : undefined, children: label }), _jsx("span", { className: "lattice-loader__text", "data-active": status === 'done' ? '' : undefined, children: doneLabel }), _jsx("span", { className: "lattice-loader__text", "data-active": status === 'error' ? '' : undefined, children: errorLabel })] }), showTimer ? (_jsx("span", { ref: timerRef, className: "lattice-loader__timer", "aria-hidden": "true", children: "0.0s" })) : null, _jsx("span", { className: "lattice-loader__sr", children: announce })] }));
};
export default LatticeLoader;
